/**
 * Base query key structure returned by createQueryKeys
 */
type BaseQueryKeys = {
  all: readonly string[]
  lists: () => readonly unknown[]
  list: (params: unknown) => readonly unknown[]
  details: () => readonly unknown[]
  detail: (id: string) => readonly unknown[]
}

/**
 * Options for extending the query keys factory
 */
interface CreateQueryKeysOptions<E extends Record<string, (...args: any[]) => readonly unknown[]>> {
  /** Override the base key array (defaults to [feature]) */
  baseKey?: readonly string[]
  /** Custom methods to add beyond standard list/detail */
  extend?: (base: BaseQueryKeys) => E
}

/**
 * Factory function to create consistent query keys for React Query.
 * Reduces boilerplate across feature hooks.
 *
 * @example
 * // Simple usage (unchanged)
 * export const contactsKeys = createQueryKeys("contacts")
 *
 * // With custom methods
 * export const leadsKeys = createQueryKeys("leads", {
 *   extend: (base) => ({
 *     live: (division: string) => [...base.all, "live", division] as const,
 *   }),
 * })
 *
 * // With custom base key
 * export const outstandingItemsKeys = createQueryKeys("outstandingItems", {
 *   baseKey: ["outstanding_items"],
 * })
 */
export function createQueryKeys<
  T extends string,
  E extends Record<string, (...args: any[]) => readonly unknown[]> = Record<string, never>
>(feature: T, options?: CreateQueryKeysOptions<E>): BaseQueryKeys & E {
  const baseKey = options?.baseKey ?? [feature]

  const base: BaseQueryKeys = {
    all: baseKey as readonly string[],
    lists: () => [...baseKey, "list"] as const,
    list: (params: unknown) => [...baseKey, "list", params] as const,
    details: () => [...baseKey, "detail"] as const,
    detail: (id: string) => [...baseKey, "detail", id] as const,
  }

  const extensions = options?.extend?.(base) ?? ({} as E)

  return { ...base, ...extensions }
}

// Pre-built keys for common features
export const contactsKeys = createQueryKeys("contacts")
export const mattersKeys = createQueryKeys("matters")
export const tasksKeys = createQueryKeys("tasks")
export const requestsKeys = createQueryKeys("requests")
export const callsKeys = createQueryKeys("calls")
export const voicemailKeys = createQueryKeys("voicemail", {
  baseKey: ["voicemails"],
})
export const documentsKeys = createQueryKeys("documents")
export const emailsKeys = createQueryKeys("emails")
export const notificationKeys = createQueryKeys("notifications")
export const notificationPreferencesKeys = createQueryKeys("notificationPreferences")
export const systemTicketKeys = createQueryKeys("systemTickets")

// Keys with custom methods
export const leadsKeys = createQueryKeys("leads", {
  extend: (base) => ({
    live: (division: string) => [...base.all, "live", division] as const,
  }),
})

export const approvalsKeys = createQueryKeys("approvals", {
  extend: (base) => ({
    pending: () => [...base.all, "pending"] as const,
    mine: (userId: string) => [...base.all, "mine", userId] as const,
    byMatter: (matterId: string) => [...base.all, "matter", matterId] as const,
  }),
})

export const eventsKeys = createQueryKeys("events", {
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
  }),
})

export const eventTemplatesKeys = createQueryKeys("eventTemplates", {
  baseKey: ["event_templates"],
})

export const textThreadsKeys = createQueryKeys("textThreads", {
  baseKey: ["textThreads"],
})

export const signingDocumentKeys = createQueryKeys("signingDocuments", {
  baseKey: ["signing-documents"],
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
  }),
})

export const customViewKeys = createQueryKeys("customViews", {
  baseKey: ["customViews"],
})

export const itemsKeys = createQueryKeys("items", {
  baseKey: ["outstanding_items"],
})

export const documentUploadKeys = createQueryKeys("documentUploads", {
  baseKey: ["matter_document_uploads"],
})

// Matter-scoped task keys (different from global tasksKeys)
export const matterTasksKeys = createQueryKeys("matterTasks", {
  baseKey: ["tasks"],
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
    matterStep: (matterId: string, stepId: string) =>
      [...base.all, "matter", matterId, "step", stepId] as const,
    matterMine: (matterId: string, userId: string) =>
      [...base.all, "matter", matterId, "mine", userId] as const,
    matterAll: (matterId: string) => [...base.all, "matter", matterId, "all"] as const,
    matterFinished: (matterId: string) =>
      [...base.all, "matter", matterId, "finished"] as const,
  }),
})

// Pipeline keys
export const pipelineKeys = createQueryKeys("pipeline", {
  extend: (base) => ({
    pipelines: (userId: string) => [...base.all, "pipelines", userId] as const,
    workflowStep: (id: string) => [...base.all, "workflow_step", id] as const,
    columnMatters: (stepId: string, userId: string, sort: string, direction: string) =>
      [...base.all, "column", stepId, userId, sort, direction] as const,
  }),
})

// Calendar keys
export const calendarKeys = createQueryKeys("calendar", {
  extend: (base) => ({
    events: () => [...base.all, "events"] as const,
    eventRange: (start: number, end: number, userId: string) =>
      [...base.all, "events", start, end, userId] as const,
  }),
})

// Billing keys
export const billingKeys = createQueryKeys("billing", {
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
    trustHistory: (matterId: string) => [...base.all, "trust", matterId] as const,
  }),
})

export const trusteeUploadKeys = createQueryKeys("trusteeUploads", {
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
  }),
})

// Contact overview keys
export const contactOverviewKeys = createQueryKeys("contactOverview", {
  baseKey: ["contacts", "overview"],
})

// Email compose keys (non-standard structure)
export const emailComposeKeys = createQueryKeys("emailCompose", {
  baseKey: ["emails"],
  extend: (base) => ({
    compose: (params: unknown) => [...base.all, "compose", params] as const,
    templates: (filter?: { workflow?: string; division?: string }) =>
      [...base.all, "templates", filter] as const,
    senders: (filter?: { division?: string }) => [...base.all, "senders", filter] as const,
  }),
})

// Directory keys
export const directoryKeys = createQueryKeys("directory", {
  extend: (base) => ({
    bookingLinks: () => [...base.all, "bookingLinks"] as const,
    bookingLinksList: (divisionId: string) =>
      [...base.all, "bookingLinks", divisionId] as const,
  }),
})

// General items keys
export const generalItemsKeys = createQueryKeys("generalItems", {
  extend: (base) => ({
    pending: () => [...base.all, "pending"] as const,
    byContact: (contactId: string) => [...base.all, "contact", contactId] as const,
  }),
})

// Claimables keys
export const claimablesKeys = createQueryKeys("claimables", {
  extend: (base) => ({
    unclaimed: (divisionId: string) => [...base.all, "unclaimed", divisionId] as const,
    allClaimables: (divisionId: string) => [...base.all, "all", divisionId] as const,
    templates: (workflowId?: string) => [...base.all, "templates", workflowId] as const,
    byMatter: (matterId: string) => [...base.all, "matter", matterId] as const,
  }),
})

// Time Tracking keys
export const billingCategoriesKeys = createQueryKeys("billingCategories", {
  baseKey: ["billing_categories"],
  extend: (base) => ({
    division: (divisionId: string) => [...base.all, "division", divisionId] as const,
  }),
})

export const timeEntriesKeys = createQueryKeys("timeEntries", {
  baseKey: ["time_entries"],
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
    timesheet: (userId: string, weekStart: string) =>
      [...base.all, "timesheet", userId, weekStart] as const,
    mySummary: () => [...base.all, "my_summary"] as const,
    timer: () => [...base.all, "timer"] as const,
  }),
})

export const timeEntryTemplatesKeys = createQueryKeys("timeEntryTemplates", {
  baseKey: ["time_entry_templates"],
  extend: (base) => ({
    division: (divisionId: string) => [...base.all, "division", divisionId] as const,
  }),
})

export const rateCardsKeys = createQueryKeys("rateCards", {
  baseKey: ["rate_cards"],
  extend: (base) => ({
    division: (divisionId: string) => [...base.all, "division", divisionId] as const,
  }),
})

// Billing automation keys
export const billingAutomationKeys = createQueryKeys("billingAutomation", {
  baseKey: ["billing_automation_instances"],
})

// PSF module keys
export const psfKeys = createQueryKeys("psf", {
  extend: (base) => ({
    intake: (matterId: string) => [...base.all, "intake", matterId] as const,
  }),
})

// BK module keys
export const bkItemsKeys = createQueryKeys("bkItems", {
  baseKey: ["bk", "outstanding_items"],
})

export const bkHearingsKeys = createQueryKeys("bkHearings", {
  baseKey: ["bk", "hearings"],
})

export const newCaseEntriesKeys = createQueryKeys("newCaseEntries", {
  baseKey: ["bk", "new_case_entries"],
})

export const trusteeDocsGlobalKeys = createQueryKeys("trusteeDocsGlobal", {
  baseKey: ["bk", "trustee_documents_global"],
})

export const bkCaseKeys = createQueryKeys("bkCase", {
  baseKey: ["bk"],
  extend: (base) => ({
    case: (matterId: string) => [...base.all, "case", matterId] as const,
    caseInfo: (matterId: string) => [...base.all, "case-info", matterId] as const,
    creditors: (matterId: string) => [...base.all, "creditors", matterId] as const,
    garnishments: (matterId: string) => [...base.all, "garnishments", matterId] as const,
    garnishment: (id: string) => [...base.all, "garnishment", id] as const,
    objections: (matterId: string) => [...base.all, "objections", matterId] as const,
    objection: (id: string) => [...base.all, "objection", id] as const,
    docketEntries: (courtCode: string, caseNumber: string) =>
      [...base.all, "docket-entries", courtCode, caseNumber] as const,
    claims: (courtCode: string, caseNumber: string) =>
      [...base.all, "claims", courtCode, caseNumber] as const,
    creditReports: (matterId: string) => [...base.all, "credit-reports", matterId] as const,
    trusteeUploads: (matterId: string) => [...base.all, "trustee-uploads", matterId] as const,
    trusteeUpload: (id: string) => [...base.all, "trustee-upload", id] as const,
  }),
})

// Item templates keys
export const itemTemplatesKeys = createQueryKeys("itemTemplates", {
  baseKey: ["outstanding_item_templates"],
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
  }),
})

// Approval sub-feature keys
export const approvalTemplatesKeys = createQueryKeys("approvalTemplates", {
  baseKey: ["approval-templates"],
})

export const approvalCommentsKeys = createQueryKeys("approvalComments", {
  baseKey: ["approval-comments"],
  extend: (base) => ({
    byApproval: (approvalId: string) =>
      [...base.all, "list", "approval", approvalId] as const,
  }),
})

export const workflowRolesKeys = createQueryKeys("workflowRoles", {
  baseKey: ["workflow-roles"],
  extend: (base) => ({
    byWorkflow: (workflowId: string) => [...base.all, "list", workflowId] as const,
  }),
})

// Matter-specific keys
export const matterItemsKeys = createQueryKeys("matterItems", {
  baseKey: ["outstanding_items"],
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
    matterOutstanding: (matterId: string) =>
      [...base.all, "matter", matterId, "outstanding"] as const,
    matterCompleted: (matterId: string) =>
      [...base.all, "matter", matterId, "completed"] as const,
    matterSearch: (matterId: string, filters: Record<string, unknown>) =>
      [...base.all, "matter", matterId, "search", filters] as const,
  }),
})

export const feedKeys = createQueryKeys("feed", {
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
    matterFiltered: (matterId: string, filter: unknown) =>
      [...base.all, "matter", matterId, filter] as const,
  }),
})

export const portalKeys = createQueryKeys("portal", {
  extend: (base) => ({
    entry: (matterId: string) => [...base.all, "entry", matterId] as const,
    questionnaire: (matterId: string) => [...base.all, "questionnaire", matterId] as const,
    questionnaireConfig: () => [...base.all, "questionnaireConfig"] as const,
    ccc: (matterId: string) => [...base.all, "ccc", matterId] as const,
    creditors: (matterId: string) => [...base.all, "creditors", matterId] as const,
  }),
})

export const communicationAnalyticsKeys = createQueryKeys("communicationAnalytics", {
  baseKey: ["communication-analytics"],
  extend: (base) => ({
    detail: (matterId: string) => [...base.all, matterId] as const,
  }),
})

export const workflowDataKeys = createQueryKeys("workflowData", {
  baseKey: ["workflow"],
  extend: (base) => ({
    contacts: () => [...base.all, "contacts"] as const,
    contactsList: (workflowId: string) => [...base.all, "contacts", workflowId] as const,
    roles: () => [...base.all, "roles"] as const,
    rolesList: (workflowId: string) => [...base.all, "roles", workflowId] as const,
  }),
})

export const matterDocumentsKeys = createQueryKeys("matterDocuments", {
  baseKey: ["documents"],
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
    matterList: (matterId: string) => [...base.all, "matter", matterId, "list"] as const,
    matterPinned: (matterId: string) => [...base.all, "matter", matterId, "pinned"] as const,
    matterCategories: (matterId: string) =>
      [...base.all, "matter", matterId, "categories"] as const,
    matterSigning: (matterId: string) => [...base.all, "matter", matterId, "signing"] as const,
  }),
})

export const matterFormsKeys = createQueryKeys("matterForms", {
  baseKey: ["forms"],
  extend: (base) => ({
    division: (divisionId: string) => [...base.all, "division", divisionId] as const,
    workflow: (divisionId: string, workflowId: string) => [...base.all, "workflow", divisionId, workflowId] as const,
    answers: (matterId: string) => [...base.all, "answers", matterId] as const,
    submissions: (matterId: string) => [...base.all, "submissions", matterId] as const,
  }),
})

export const customFieldKeys = createQueryKeys("customFields", {
  extend: (base) => ({
    division: (divisionId: string) => [...base.all, "division", divisionId] as const,
  }),
})

export const automationKeys = createQueryKeys("automations", {
  extend: (base) => ({
    logs: (matterId: string) => [...base.all, "logs", matterId] as const,
    assignments: (matterId: string) => [...base.all, "assignments", matterId] as const,
    instances: (matterId: string) => [...base.all, "instances", matterId] as const,
    available: (matterId: string) => [...base.all, "available", matterId] as const,
    scheduledTexts: (matterId: string) => [...base.all, "scheduledTexts", matterId] as const,
    followUpThreads: (matterId: string) => [...base.all, "followUpThreads", matterId] as const,
  }),
})

// Signing templates keys
export const signingTemplatesKeys = createQueryKeys("signingTemplates", {
  baseKey: ["signing-templates"],
  extend: (base) => ({
    config: () => [...base.all, "config"] as const,
    matterData: (matterId: string) => [...base.all, "matter-data", matterId] as const,
    fieldOptions: (divisionId: string) => [...base.all, "field-options", divisionId] as const,
  }),
})

// Queue metrics keys
export const queueMetricsKeys = createQueryKeys("queueMetrics", {
  baseKey: ["queue-metrics"],
  extend: (base) => ({
    today: () => [...base.all, "today"] as const,
  }),
})

// Settings keys
export const currentUserKeys = createQueryKeys("currentUser", {
  extend: (base) => ({
    detail: () => [...base.all, "detail"] as const,
  }),
})

export const emailGrantsKeys = createQueryKeys("emailGrants", {
  extend: (base) => ({
    list: () => [...base.all, "list"] as const,
  }),
})

// Dashboard keys
export const dashboardKeys = createQueryKeys("dashboard", {
  extend: (base) => ({
    overview: (divisionId: string) => [...base.all, "overview", divisionId] as const,
    sidebar: (divisionId: string) => [...base.all, "sidebar", divisionId] as const,
  }),
})

// BK Item Type Configs keys
export const bkItemTypeConfigsKeys = createQueryKeys("bkItemTypeConfigs", {
  baseKey: ["bk", "item_type_configs"],
  extend: (base) => ({
    workflow: (workflowId: string, type?: string, chapter?: number) =>
      [...base.all, "workflow", workflowId, type, chapter] as const,
  }),
})

// Mentions keys
export const mentionsKeys = createQueryKeys("mentions", {
  baseKey: ["user_comments"],
  extend: (base) => ({
    count: (divisionId: string) => [...base.all, "count", divisionId] as const,
  }),
})

// AI keys
export const aiThreadsKeys = createQueryKeys("aiThreads", {
  baseKey: ["ai_threads"],
  extend: (base) => ({
    matter: (matterId: string) => [...base.all, "matter", matterId] as const,
    thread: (threadId: string) => [...base.all, "thread", threadId] as const,
  }),
})

export const aiJobsKeys = createQueryKeys("aiJobs", {
  baseKey: ["ai_jobs"],
  extend: (base) => ({
    division: (divisionId: string, isSystemTask?: boolean) =>
      [...base.all, "division", divisionId, isSystemTask ?? false] as const,
  }),
})

// Training keys
export const trainingKeys = createQueryKeys("training", {
  baseKey: ["training"],
  extend: (base) => ({
    myEnrollments: (params?: unknown) => [...base.all, "my_enrollments", params] as const,
    course: (courseId: string) => [...base.all, "course", courseId] as const,
    units: (courseId: string) => [...base.all, "units", courseId] as const,
    unit: (unitId: string) => [...base.all, "unit", unitId] as const,
    unitProgress: (enrollmentId: string) => [...base.all, "unit_progress", enrollmentId] as const,
    quiz: (quizId: string) => [...base.all, "quiz", quizId] as const,
    enrollment: (enrollmentId: string) => [...base.all, "enrollment", enrollmentId] as const,
    mySimulations: (params?: unknown) => [...base.all, "my_simulations", params] as const,
    simulation: (id: string) => [...base.all, "simulation", id] as const,
  }),
})

// Filing (Case Preparation) keys
export const filingKeys = createQueryKeys("filing", {
  baseKey: ["bk", "filing"],
  extend: (base) => ({
    byMatter: (matterId: string) => [...base.all, "matter", matterId] as const,
    formData: (filingId: string, formType: string) => [...base.all, "form-data", filingId, formType] as const,
    formConfig: (formType?: string) => [...base.all, "form-config", formType] as const,
    formConfigByChapter: (chapter: number) => [...base.all, "form-config-chapter", chapter] as const,
    properties: (filingId: string) => [...base.all, "properties", filingId] as const,
    meansTest: (filingId: string) => [...base.all, "means-test", filingId] as const,
    meansTestStaleness: (filingId: string) => [...base.all, "means-test-staleness", filingId] as const,
    medianIncome: (filingId: string) => [...base.all, "median-income", filingId] as const,
    meansTestDraft: (filingId: string) => [...base.all, "means-test-draft", filingId] as const,
    validation: (filingId: string) => [...base.all, "validation", filingId] as const,
    crossSchedule: (filingId: string) => [...base.all, "cross-schedule", filingId] as const,
    commonInfo: (filingId: string) => [...base.all, "common-info", filingId] as const,
    reaffirmations: (filingId: string) => [...base.all, "reaffirmations", filingId] as const,
    statementOfIntention: (filingId: string) => [...base.all, "statement-of-intention", filingId] as const,
    planData: (filingId: string) => [...base.all, "plan-data", filingId] as const,
    planDrafts: (filingId: string) => [...base.all, "plan-drafts", filingId] as const,
    planDraft: (filingId: string, draftId: string) => [...base.all, "plan-draft", filingId, draftId] as const,
    scheduleC: (filingId: string) => [...base.all, "schedule-c", filingId] as const,
    exemptionConfig: (filingId: string, state?: string) => [...base.all, "exemption-config", filingId, state] as const,
    hiddenProperties: (filingId: string) => [...base.all, "hidden-properties", filingId] as const,
    creditorOptions: (filingId: string) => [...base.all, "creditor-options", filingId] as const,
    incomeHistory: (filingId: string) => [...base.all, "income-history", filingId] as const,
    incomeHistoryPeriods: (filingId: string) => [...base.all, "income-history-periods", filingId] as const,
    incomeHistorySummary: (filingId: string) => [...base.all, "income-history-summary", filingId] as const,
    chapterComparison: (filingId: string) => [...base.all, "chapter-comparison", filingId] as const,
    districts: () => [...base.all, "districts"] as const,
    states: () => [...base.all, "states"] as const,
    counties: (state: string) => [...base.all, "counties", state] as const,
  }),
})

export const changelogKeys = createQueryKeys("changelog", {
  extend: (base) => ({
    unreadCount: () => [...base.all, "unread_count"] as const,
  }),
})

export const ssnKeys = createQueryKeys("ssn", {
  extend: (base) => ({
    byContact: (contactId: string) => [...base.all, "contact", contactId] as const,
  }),
})

export const bkWorkflowConfigKeys = createQueryKeys("bkWorkflowConfig", {
  extend: (base) => ({
    byDivisionWorkflow: (division: string, workflow: string) =>
      [...base.all, division, workflow] as const,
  }),
})
